import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  fetchInstituteSettings,
  saveInstituteSettings,
} from "../../Apis/InstituteSettings-api";

const DEFAULT_FORM = {
  institute_start_time: "09:00",
  institute_end_time: "18:00",
  counsellor_start_time: "09:00",
  counsellor_end_time: "21:00",
  total_student_capacity: 800,
  fee_reminder_days_before: 5,
  summary_cards_enabled: true,
  certificate_auto_generate: false,
  whatsapp_notifications_enabled: false,
  email_notifications_enabled: false,
  sms_notifications_enabled: false,
  placement_required_enabled: false,
};

const normalizeTimeForInput = (value) => {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return s;
  const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
  const min = Math.min(59, Math.max(0, parseInt(match[2], 10)));
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

function InstituteSettingsDialog({ open, onClose, organizationId }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateUrl, setTemplateUrl] = useState("");

  useEffect(() => {
    if (!open || !organizationId) return;

    let cancelled = false;
    setLoading(true);
    setErrors({});
    setTemplateFile(null);

    fetchInstituteSettings(organizationId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        if (data) {
          setForm({
            institute_start_time: normalizeTimeForInput(data.institute_start_time) || DEFAULT_FORM.institute_start_time,
            institute_end_time: normalizeTimeForInput(data.institute_end_time) || DEFAULT_FORM.institute_end_time,
            counsellor_start_time:
              normalizeTimeForInput(data.counsellor_start_time) || DEFAULT_FORM.counsellor_start_time,
            counsellor_end_time:
              normalizeTimeForInput(data.counsellor_end_time) || DEFAULT_FORM.counsellor_end_time,
            total_student_capacity: Number(data.total_student_capacity ?? DEFAULT_FORM.total_student_capacity),
            fee_reminder_days_before: Number(data.fee_reminder_days_before ?? DEFAULT_FORM.fee_reminder_days_before),
            summary_cards_enabled:
              data.summary_cards_enabled == null
                ? DEFAULT_FORM.summary_cards_enabled
                : !!data.summary_cards_enabled,
            certificate_auto_generate: !!data.certificate_auto_generate,
            whatsapp_notifications_enabled: !!data.whatsapp_notifications_enabled,
            email_notifications_enabled: !!data.email_notifications_enabled,
            sms_notifications_enabled: !!data.sms_notifications_enabled,
            placement_required_enabled: !!data.placement_required_enabled,
          });
          const storedTemplate = String(data.certificate_template || "");
          const hasTemplate = storedTemplate !== "" && storedTemplate !== "training_certificate";
          setTemplateName(hasTemplate ? storedTemplate.split("/").pop() : "");
          setTemplateUrl(hasTemplate ? data.certificate_template_url || "" : "");
        } else {
          setForm(DEFAULT_FORM);
          setTemplateName("");
          setTemplateUrl("");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load institute settings."
        );
        setForm(DEFAULT_FORM);
        setTemplateName("");
        setTemplateUrl("");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, organizationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSwitch = (name) => (e) => {
    setForm((prev) => ({ ...prev, [name]: e.target.checked }));
  };

  const validate = () => {
    const next = {};
    if (!form.institute_start_time) {
      next.institute_start_time = "Start time is required.";
    }
    if (!form.institute_end_time) {
      next.institute_end_time = "End time is required.";
    } else if (
      form.institute_start_time &&
      form.institute_end_time <= form.institute_start_time
    ) {
      next.institute_end_time = "End time must be after start time.";
    }
    if (!form.counsellor_start_time) {
      next.counsellor_start_time = "Counsellor start time is required.";
    }
    if (!form.counsellor_end_time) {
      next.counsellor_end_time = "Counsellor end time is required.";
    } else if (
      form.counsellor_start_time &&
      form.counsellor_end_time <= form.counsellor_start_time
    ) {
      next.counsellor_end_time = "End time must be after start time.";
    }
    const capacity = Number(form.total_student_capacity);
    if (!capacity || capacity < 1) {
      next.total_student_capacity = "Capacity must be at least 1.";
    }
    const reminderDays = Number(form.fee_reminder_days_before);
    if (Number.isNaN(reminderDays) || reminderDays < 0) {
      next.fee_reminder_days_before = "Reminder days cannot be negative.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!organizationId) {
      toast.error("No active organization selected.");
      return;
    }
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        organization_id: Number(organizationId),
        institute_start_time: form.institute_start_time,
        institute_end_time: form.institute_end_time,
        counsellor_start_time: form.counsellor_start_time,
        counsellor_end_time: form.counsellor_end_time,
        total_student_capacity: Number(form.total_student_capacity),
        fee_reminder_days_before: Number(form.fee_reminder_days_before),
        summary_cards_enabled: !!form.summary_cards_enabled,
        certificate_auto_generate: !!form.certificate_auto_generate,
        whatsapp_notifications_enabled: !!form.whatsapp_notifications_enabled,
        email_notifications_enabled: !!form.email_notifications_enabled,
        sms_notifications_enabled: !!form.sms_notifications_enabled,
        placement_required_enabled: !!form.placement_required_enabled,
      };
      if (templateFile) {
        const body = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (typeof value === "boolean") {
            body.append(key, value ? "1" : "0");
          } else {
            body.append(key, value ?? "");
          }
        });
        body.append("certificate_template", templateFile);
        await saveInstituteSettings(body, true);
      } else {
        await saveInstituteSettings(payload);
      }
      window.dispatchEvent(
        new CustomEvent("ems-institute-settings-updated", {
          detail: {
            organizationId: Number(organizationId),
            summaryCardsEnabled: payload.summary_cards_enabled,
            placementRequiredEnabled: payload.placement_required_enabled,
          },
        })
      );
      toast.success("Institute settings saved successfully.");
      onClose?.();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, messages]) => {
          mapped[key] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setErrors(mapped);
      }
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save institute settings."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="institute-settings-title"
    >
      <DialogTitle id="institute-settings-title">Institute Settings</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box component="form" noValidate>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Configure institute hours, counsellor follow-up hours, capacity, list cards, fee reminders, and notification preferences.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Institute Start Time"
                  name="institute_start_time"
                  type="time"
                  value={form.institute_start_time}
                  onChange={handleChange}
                  error={!!errors.institute_start_time}
                  helperText={errors.institute_start_time}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  label="Institute End Time"
                  name="institute_end_time"
                  type="time"
                  value={form.institute_end_time}
                  onChange={handleChange}
                  error={!!errors.institute_end_time}
                  helperText={errors.institute_end_time}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Typography variant="subtitle1" fontWeight={600}>
                Counsellor Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Follow-ups are placed inside these hours. Default is 9:00 AM to 9:00 PM.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Counsellor Start Time"
                  name="counsellor_start_time"
                  type="time"
                  value={form.counsellor_start_time}
                  onChange={handleChange}
                  error={!!errors.counsellor_start_time}
                  helperText={errors.counsellor_start_time}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  label="Counsellor End Time"
                  name="counsellor_end_time"
                  type="time"
                  value={form.counsellor_end_time}
                  onChange={handleChange}
                  error={!!errors.counsellor_end_time}
                  helperText={errors.counsellor_end_time}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Total Student Capacity"
                  name="total_student_capacity"
                  type="number"
                  value={form.total_student_capacity}
                  onChange={handleChange}
                  error={!!errors.total_student_capacity}
                  helperText={errors.total_student_capacity}
                  inputProps={{ min: 1 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  label="Fee Reminder (days before due)"
                  name="fee_reminder_days_before"
                  type="number"
                  value={form.fee_reminder_days_before}
                  onChange={handleChange}
                  error={!!errors.fee_reminder_days_before}
                  helperText={errors.fee_reminder_days_before}
                  inputProps={{ min: 0 }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              List Display
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={form.summary_cards_enabled}
                  onChange={handleSwitch("summary_cards_enabled")}
                />
              }
              label="Show summary cards on Leads, Students, Fees, and Batches lists"
            />

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Certificate
            </Typography>
            <Button variant="outlined" component="label" sx={{ textTransform: "none" }}>
              Upload template
              <input
                hidden
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.html,.htm,image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setTemplateFile(file);
                  e.target.value = "";
                }}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1, color: "var(--ems-muted, #6b7280)" }}>
              {templateFile
                ? templateFile.name
                : templateName
                  ? "Current template: "
                  : "No template uploaded yet."}
              {!templateFile && templateName ? (
                templateUrl ? (
                  <a href={templateUrl} target="_blank" rel="noreferrer">
                    {templateName}
                  </a>
                ) : (
                  templateName
                )
              ) : null}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: "var(--ems-muted, #6b7280)" }}>
              Upload a JPG, PNG, or other photo of the certificate. That image is used for every student certificate in this organization.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.certificate_auto_generate}
                  onChange={handleSwitch("certificate_auto_generate")}
                />
              }
              label="Auto-generate certificates on completion"
            />

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Automation &amp; Notifications
            </Typography>

            <Box display="flex" flexDirection="column" gap={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.whatsapp_notifications_enabled}
                    onChange={handleSwitch("whatsapp_notifications_enabled")}
                  />
                }
                label="WhatsApp notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.email_notifications_enabled}
                    onChange={handleSwitch("email_notifications_enabled")}
                  />
                }
                label="Email notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.sms_notifications_enabled}
                    onChange={handleSwitch("sms_notifications_enabled")}
                  />
                }
                label="SMS notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.placement_required_enabled}
                    onChange={handleSwitch("placement_required_enabled")}
                  />
                }
                label="Does Institute provide Placement Assistance"
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || saving || !organizationId}
          sx={{ textTransform: "none", fontWeight: 650 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InstituteSettingsDialog;
